import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
} from 'rxjs';
import { MapComponent, MapView } from 'src/app/components/map.component';
import {
  ChatLinkDialogComponent,
  ChatLinkDialogData,
  ChatLinkDialogResult,
} from 'src/app/components/chat-link-dialog/chat-link-dialog.component';
import {
  NEW_WINDOW_MS,
  isNew,
  isOnline,
  timeframeMs,
  timeframeWords,
} from 'src/app/components/map-pins';
import { MemberMapService } from 'src/app/services/member-map.service';
import {
  ChatLink,
  MapMember,
  MapSpace,
  SpaceIndexRow,
  SpaceWindow,
} from 'src/app/data-transfer-objects/member-map.dto';

type TimeframeOption = { name: string; value: string };
type SpaceOption = { name: string; value: SpaceWindow | null };
/** What the spaces field can hold: a layer, or one space picked by name. */
type SpacePick = SpaceOption | SpaceIndexRow;

/** Options rendered at once; typing narrows the rest. */
const PICKER_LIMIT = 100;

@Component({
  selector: 'app-users-map',
  templateUrl: './users-map.component.html',
  styleUrls: ['./users-map.component.scss'],
  imports: [
    MapComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatInputModule,
    MatCardModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressBarModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatSnackBarModule,
  ],
  standalone: true,
})
export class UsersMapComponent implements OnInit, OnDestroy {
  isLoading = false;

  timeframeOptions: Array<TimeframeOption> = [
    { name: '1 hour ago', value: '1h' },
    { name: '2 hours ago', value: '2h' },
    { name: '4 hours ago', value: '4h' },
    { name: '8 hours ago', value: '8h' },
    { name: '12 hours ago', value: '12h' },
    { name: '1 day ago', value: '1d' },
    { name: '7 days ago', value: '7d' },
    { name: '14 days ago', value: '14d' },
    { name: '30 days ago', value: '30d' },
    { name: 'Dawn of time', value: '' },
  ];

  spaceOptions: Array<SpaceOption> = [
    { name: 'Off', value: null },
    { name: 'Happening now', value: 'now' },
    { name: 'Next 7 days', value: 'week' },
    { name: 'Last 30 days', value: 'month' },
    { name: 'All spaces', value: 'all' },
  ];

  filterBy: 'lastSeen' | 'createdAt' = 'lastSeen';
  selectedTimeframe: TimeframeOption = this.timeframeOptions[5];
  spaceWindow: SpaceWindow | null = 'now';
  showChats = false;

  members: MapMember[] = [];
  spaces: MapSpace[] = [];
  /**
   * The spaces actually drawn. With a timeframe set, a space shows only while
   * at least one of its members is on the map: a space nobody from "1 day
   * ago" belongs to is not part of that picture. Kept as a field, not a
   * getter, so the map is not handed a new array on every check.
   */
  shownSpaces: MapSpace[] = [];
  located = 0;
  unlocated = 0;
  truncated = false;

  /** Every pair that has chatted, loaded once when the layer is first shown. */
  private allLinks: ChatLink[] | null = null;
  private linksLoading = false;
  chatLinks: ChatLink[] | null = null;
  selectedSpace: MapSpace | null = null;

  /** Every space with a member, newest created first, for the picker. */
  spaceIndex: SpaceIndexRow[] = [];
  spaceQuery = new FormControl<string | SpacePick>('', { nonNullable: true });
  filteredSpaces: SpaceIndexRow[] = [];
  /** Layers whose name matches what was typed; all of them when nothing was. */
  filteredLayers: SpaceOption[] = this.spaceOptions;
  moreSpaces = 0;

  @ViewChild(MapComponent) mapView?: MapComponent;

  /** Find-a-member box: name, company, role or city. */
  memberQuery = new FormControl<string | MapMember>('', { nonNullable: true });
  memberResults: MapMember[] = [];
  memberSearching = false;

  /** The view from the URL, handed to the map once. */
  initialView: MapView | null = null;
  private view: MapView | null = null;
  private memberSub?: Subscription;

  private request?: Subscription;
  private querySub?: Subscription;

  constructor(
    private mapService: MemberMapService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  get onlineCount(): number {
    const now = Date.now();
    return this.members.filter((m) => isOnline(m, now)).length;
  }

  /** Spaces that have a pin; a picked space with no location has none. */
  get pinnedSpaces(): number {
    return this.shownSpaces.filter((sp) => sp.lat != null && sp.lng != null).length;
  }

  get newCount(): number {
    const now = Date.now();
    const within = this.newWithinMs;
    return this.members.filter((m) => isNew(m, now, within)).length;
  }

  /**
   * "New" means joined within the timeframe being looked at, so "7 days ago"
   * counts the same joiners whether the filter is last seen or joined.
   * Dawn of time has no window and falls back to 14 days.
   */
  get newWithinMs(): number {
    return timeframeMs(this.selectedTimeframe.value) ?? NEW_WINDOW_MS;
  }

  /** The window in words, for the stats strip: "7 days", "12 hours". */
  get newWindowLabel(): string {
    return timeframeWords(this.selectedTimeframe.value);
  }

  ngOnInit(): void {
    this.readUrl();
    this.memberSub = this.memberQuery.valueChanges
      .pipe(
        map((v) => (typeof v === 'string' ? v.trim() : '')),
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((q) => {
          if (q.length < 2) return of([] as MapMember[]);
          this.memberSearching = true;
          return this.mapService.searchMembers(q).pipe(
            map((r) => r.members),
            catchError(() => of([] as MapMember[]))
          );
        })
      )
      .subscribe((found) => {
        this.memberResults = found;
        this.memberSearching = false;
      });
    this.load();
    this.syncSpaceField();
    this.querySub = this.spaceQuery.valueChanges.subscribe((v) =>
      this.filterSpaces(typeof v === 'string' ? v : '')
    );
    this.mapService.getSpaceIndex().subscribe({
      next: (res) => {
        this.spaceIndex = res.spaces;
        this.filterSpaces('');
      },
      error: () => {
        // The picker still offers the layers; only by-name picking is lost.
        this.spaceIndex = [];
      },
    });
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.querySub?.unsubscribe();
    this.memberSub?.unsubscribe();
  }

  /* ---------- find a member ---------- */

  memberLabel = (m: MapMember | string | null): string =>
    !m ? '' : typeof m === 'string' ? m : `${m.firstName} ${m.lastName}`.trim();

  memberLine(m: MapMember): string {
    return [m.role, m.company, m.city].filter((x) => x && x.trim()).join(' · ');
  }

  /**
   * "Nobody matches" is for a search that found nobody - not for the name of
   * the member just picked, which sits in the box as an object.
   */
  get noMemberMatch(): boolean {
    const v = this.memberQuery.value;
    return typeof v === 'string' && v.trim().length >= 2 && !this.memberSearching && !this.memberResults.length;
  }

  clearMemberSearch(): void {
    this.memberQuery.setValue('');
    this.memberResults = [];
    this.mapView?.clearFocus();
  }

  /** Anything that differs from how the map opens. */
  get hasFilters(): boolean {
    return (
      this.filterBy !== 'lastSeen' ||
      this.selectedTimeframe.value !== '1d' ||
      this.spaceWindow !== 'now' ||
      !!this.selectedSpace ||
      this.showChats ||
      !!this.memberLabel(this.memberQuery.value)
    );
  }

  /** Back to how the map opens: last seen, 1 day, spaces happening now, no chat lines. */
  resetFilters(): void {
    this.filterBy = 'lastSeen';
    this.selectedTimeframe = this.timeframeOptions.find((o) => o.value === '1d') ?? this.selectedTimeframe;
    this.spaceWindow = 'now';
    this.selectedSpace = null;
    this.showChats = false;
    this.chatLinks = null;
    this.clearMemberSearch();
    this.syncSpaceField();
    this.load();
  }

  /**
   * Fly to the member. Someone the current filter left off the map is added
   * to it first, so the search finds anyone with a location.
   */
  onMemberPicked(m: MapMember): void {
    if (!this.members.some((x) => x.id === m.id)) {
      this.members = [...this.members, m];
      this.refreshLinks();
    }
    // After the map has taken the new members in.
    setTimeout(() => this.mapView?.focusMember(m.id));
  }

  /* ---------- the URL ---------- */

  /*
   * Filters, the picked space, the chat layer and the view live in the query
   * string, so a view can be bookmarked or sent and survives a reload.
   * Defaults are left out to keep the link short.
   */
  private readUrl(): void {
    const q = this.route.snapshot.queryParamMap;
    if (q.get('by') === 'joined') this.filterBy = 'createdAt';
    const tf = q.get('tf');
    if (tf !== null) {
      const want = tf === 'all' ? '' : tf;
      this.selectedTimeframe = this.timeframeOptions.find((o) => o.value === want) ?? this.selectedTimeframe;
    }
    const layer = q.get('spaces');
    if (layer !== null) {
      this.spaceWindow = layer === 'off' ? null : this.spaceOptions.find((o) => o.value === layer)?.value ?? this.spaceWindow;
    }
    const space = q.get('space');
    if (space) {
      this.selectedSpace = {
        id: space,
        title: '',
        logo: null,
        lat: null,
        lng: null,
        startDate: null,
        endDate: null,
        memberIds: [],
      };
    }
    if (q.get('chats') === '1') this.showChats = true;
    const at = (q.get('at') ?? '').split(',').map(Number);
    if (at.length === 3 && at.every((n) => Number.isFinite(n))) {
      this.initialView = { lat: at[0], lng: at[1], zoom: at[2] };
      this.view = this.initialView;
    }
  }

  private writeUrl(): void {
    const v = this.view;
    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl: true,
      queryParams: {
        by: this.filterBy === 'createdAt' ? 'joined' : null,
        tf: this.selectedTimeframe.value === '1d' ? null : this.selectedTimeframe.value || 'all',
        spaces: this.spaceWindow === 'now' ? null : this.spaceWindow ?? 'off',
        space: this.selectedSpace?.id ?? null,
        chats: this.showChats ? '1' : null,
        at: v ? `${v.lat},${v.lng},${v.zoom}` : null,
      },
    });
  }

  onViewChanged(v: MapView): void {
    this.view = v;
    this.writeUrl();
  }

  /* ---------- the spaces field ---------- */

  isLayer(pick: SpacePick): pick is SpaceOption {
    return !('memberCount' in pick);
  }

  spaceLabel = (pick: SpacePick | string | null): string => {
    if (!pick) return '';
    if (typeof pick === 'string') return pick;
    return this.isLayer(pick) ? pick.name : pick.title;
  };

  pickedDate(row: SpaceIndexRow): string {
    const iso = row.startDate ?? row.createdAt;
    return iso
      ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
      : '';
  }

  onSpacePicked(pick: SpacePick): void {
    if (this.isLayer(pick)) {
      this.spaceWindow = pick.value;
      this.selectedSpace = null;
      this.load();
      return;
    }
    this.selectSpace({
      id: pick.id,
      title: pick.title,
      logo: null,
      lat: null,
      lng: null,
      startDate: pick.startDate,
      endDate: null,
      memberIds: [],
    });
  }

  /** Put the current choice back in the field, e.g. after typing and leaving. */
  syncSpaceField(): void {
    const current: SpacePick | undefined = this.selectedSpace
      ? this.spaceIndex.find((r) => r.id === this.selectedSpace!.id) ?? {
          id: this.selectedSpace.id,
          title: this.selectedSpace.title,
          startDate: this.selectedSpace.startDate,
          createdAt: null,
          memberCount: this.selectedSpace.memberIds.length,
          located: this.selectedSpace.lat != null,
        }
      : this.spaceOptions.find((o) => o.value === this.spaceWindow);
    this.spaceQuery.setValue(current ?? '', { emitEvent: false });
    this.filterSpaces('');
  }

  private filterSpaces(text: string): void {
    const q = text.trim().toLowerCase();
    const matching = q
      ? this.spaceIndex.filter((r) => r.title.toLowerCase().includes(q))
      : this.spaceIndex;
    this.filteredLayers = q
      ? this.spaceOptions.filter((o) => o.name.toLowerCase().includes(q))
      : this.spaceOptions;
    this.filteredSpaces = matching.slice(0, PICKER_LIMIT);
    this.moreSpaces = Math.max(0, matching.length - PICKER_LIMIT);
  }

  /* ---------- chat popup ---------- */

  onChatLinkClicked(links: ChatLink[]): void {
    const data: ChatLinkDialogData = {
      links,
      members: new Map(this.members.map((m) => [m.id, m])),
      newWithinMs: this.newWithinMs,
    };
    this.dialog
      .open<ChatLinkDialogComponent, ChatLinkDialogData, ChatLinkDialogResult>(
        ChatLinkDialogComponent,
        { data, width: '760px', maxWidth: '95vw', autoFocus: false }
      )
      .afterClosed()
      .subscribe((result) => {
        if (result?.focus?.length) this.mapView?.focusMembers(result.focus);
      });
  }

  load(): void {
    this.request?.unsubscribe();
    this.isLoading = true;
    this.request = this.mapService
      .getMap({
        field: this.filterBy,
        timeframe: this.selectedTimeframe.value,
        spaces: this.spaceWindow,
        space: this.selectedSpace?.id ?? null,
      })
      .subscribe({
        next: (res) => {
          this.members = res.members;
          this.located = res.located;
          this.unlocated = res.unlocated;
          this.truncated = res.truncated;
          if (this.selectedSpace && res.space) this.selectedSpace = res.space;
          this.spaces = this.withSelected(res.spaces);
          this.shownSpaces = this.visibleSpaces();
          this.syncSpaceField();
          this.writeUrl();
          if (this.showChats && !this.allLinks && !this.linksLoading) this.onChatsToggled();
          this.refreshLinks();
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.snackBar.open(`Could not load the map: ${err?.message ?? err}`, 'OK', {
            duration: 6000,
          });
        },
      });
  }

  onFiltersChanged(): void {
    this.load();
  }

  onChatsToggled(): void {
    this.writeUrl();
    if (!this.showChats) {
      this.chatLinks = null;
      return;
    }
    if (this.allLinks) {
      this.refreshLinks();
      return;
    }
    if (this.linksLoading) return;
    this.linksLoading = true;
    this.isLoading = true;
    this.mapService.getChatLinks().subscribe({
      next: (res) => {
        this.linksLoading = false;
        this.allLinks = res.links;
        this.refreshLinks();
        this.isLoading = false;
      },
      error: (err) => {
        this.linksLoading = false;
        this.isLoading = false;
        this.showChats = false;
        this.snackBar.open(`Could not load chat links: ${err?.message ?? err}`, 'OK', {
          duration: 6000,
        });
      },
    });
  }

  onSpaceSelected(space: MapSpace): void {
    this.selectSpace(space);
  }

  private selectSpace(space: MapSpace): void {
    if (this.selectedSpace?.id === space.id) return;
    this.selectedSpace = space;
    this.syncSpaceField();
    this.load();
  }

  clearSpace(): void {
    this.selectedSpace = null;
    this.syncSpaceField();
    this.load();
  }

  private visibleSpaces(): MapSpace[] {
    if (this.selectedSpace || !this.selectedTimeframe.value) return this.spaces;
    const here = new Set(this.members.map((m) => m.id));
    return this.spaces.filter((sp) => sp.memberIds.some((id) => here.has(id)));
  }

  /** Only links whose two ends are both on the map can be drawn. */
  private refreshLinks(): void {
    if (!this.showChats || !this.allLinks) {
      this.chatLinks = null;
      return;
    }
    const here = new Set(this.members.map((m) => m.id));
    this.chatLinks = this.allLinks.filter((l) => here.has(l.a) && here.has(l.b));
  }

  /*
   * A selected space stays on the map even when the spaces layer is off or
   * the space falls outside its window - otherwise the spokes lose their hub.
   */
  private withSelected(spaces: MapSpace[]): MapSpace[] {
    const selected = this.selectedSpace;
    if (!selected || spaces.some((s) => s.id === selected.id)) return spaces;
    return [...spaces, selected];
  }
}
